import { state } from './state.js';
import * as UI from './ui.js';
import * as ResultViewer from './result_viewer.js';
import * as API from './api.js';
import { applyHistoryRetentionMeta, upsertHistoryItem } from './history_loader.js';
import * as SourcePhotos from './source_photos.js';
import { UI_COPY } from './config/uiCopy.js';
import { refreshSettingsVisibility, requestWorkflowScroll } from './workflow_selection.js';
import { launchMosaicConfetti } from './ui/confetti.js';
import {
    applyCancelledProgressUi,
    applyCompleteProgressUi,
    applyErrorProgressUi,
    applyProgressUpdate,
    showCompleteReadyToast,
} from './job_progress_ui.js';
import { clearPendingJob } from './job_manager.js';
import { showToast } from './ui/toast.js';
import { refreshUserConfig } from './auth.js';
import { isOnboardingMosaicReadyContext } from './onboarding.js';

let _jobHandlersAttached = false;
const GUEST_SIGNIN_SOFT_PROMPT_KEY = 'mosaic_guest_signin_soft_prompt_count_v1';

function getCurrentJobElapsedMs() {
    const startedAt = Number(state.currentJobStartedAtMs || 0);
    if (!startedAt) return null;
    const elapsed = Date.now() - startedAt;
    return Number.isFinite(elapsed) && elapsed >= 0 ? elapsed : null;
}

async function refreshQuotaAfterCompletion() {
    if (state.isAnonymous) return;
    const plan = await refreshUserConfig();
    if (!plan) return;
    const limit = Number(plan.daily_job_limit || 0);
    const remaining = Number(plan.remaining_jobs_today);
    if (!Number.isFinite(remaining) || limit <= 0) return;
    if (remaining < 10) {
        if (isOnboardingMosaicReadyContext()) {
            API.logEvent('daily_quota_warning_suppressed_onboarding_ready', {
                remaining,
                limit,
                jobs_started: Number(plan.jobs_started_today || 0),
            }).catch(() => {});
            return;
        }
        showToast('Daily quota', `You have ${remaining} jobs remaining today.`, 3200);
        API.logEvent('daily_quota_warning_shown', {
            remaining,
            limit,
            jobs_started: Number(plan.jobs_started_today || 0),
        }).catch(() => {});
    }
}

function maybePromptGuestSignInAfterGeneration(jobId = '') {
    if (!state.isAnonymous) return;
    let count = 0;
    try {
        count = Number(sessionStorage.getItem(GUEST_SIGNIN_SOFT_PROMPT_KEY) || 0);
    } catch (_e) {
        count = 0;
    }
    count = Number.isFinite(count) ? count + 1 : 1;
    try {
        sessionStorage.setItem(GUEST_SIGNIN_SOFT_PROMPT_KEY, String(count));
    } catch (_e) {}
    if (count > 2) return;
    showToast(
        'Like this result?',
        'Sign in to save your mosaics and unlock download.',
        3600,
    );
    API.logEvent('guest_signin_soft_prompt_shown', {
        job_id: String(jobId || state.jobId || ''),
        generation_count: count,
    }).catch(() => {});
}

function _scrollCardToWorkflowTop() {
    requestWorkflowScroll('viewer', { source: 'job_complete', afterFrame: true });
}

export function setupJobEventHandlers() {
    if (_jobHandlersAttached) return;
    _jobHandlersAttached = true;
    document.addEventListener('job:phase', (e) => { });
    document.addEventListener('job:first_progress', (e) => {
        if (state.currentJobFirstProgressAtMs) return;
        state.currentJobFirstProgressAtMs = Date.now();
        const elapsedMs = getCurrentJobElapsedMs();
        API.logUiFlow('job', 'first_progress', 'ok', {
            job_id: (e.detail && e.detail.jobId) ? String(e.detail.jobId) : (state.jobId || ''),
            phase: (e.detail && e.detail.phase) ? String(e.detail.phase) : '',
        }, elapsedMs).catch(() => {});
    });
    document.addEventListener('job:progress', (e) => {
        const detail = e.detail || {};
        applyProgressUpdate(detail);
        ResultViewer.applyLivePreview(detail);
    });

    document.addEventListener('job:complete', async (e) => {
        const { image_url, preview_url, inspect_url, source_url, manifest, settings, tileMap, trivia } = e.detail || {};
        const jobId = (e.detail && e.detail.jobId) ? String(e.detail.jobId) : '';
        const usedSimpleDetailExtreme = String(state.currentJobSimpleDetailExtreme || '');
        const effectiveJobId = jobId || state.jobId || '';
        if (jobId) clearPendingJob(jobId);

        const stopBtn = document.getElementById('stopBtn');
        if (stopBtn) stopBtn.style.display = 'none';

        state.lastShownSettings = settings || null;
        if (tileMap) state.tileMap = tileMap;
        state.lastShownSourceInfo = {
            sourcePhotoId: (manifest && manifest.settings && manifest.settings.source_photo_id)
                ? String(manifest.settings.source_photo_id)
                : (state.selectedSourcePhotoId ? String(state.selectedSourcePhotoId) : null),
            sourceUrl: source_url || '',
            sourceKey: (state.selectedSourcePhoto && state.selectedSourcePhoto.source_key)
                ? String(state.selectedSourcePhoto.source_key)
                : '',
            canonicalSourceKey: (state.selectedSourcePhoto && state.selectedSourcePhoto.source_key)
                ? String(state.selectedSourcePhoto.source_key)
                : '',
            filename: '',
            jobId: jobId || '',
        };

        const displayUrl = preview_url || image_url;
        const inspectUrl = inspect_url || image_url;
        const downloadUrl = image_url;
        try {
            await ResultViewer.show(
                displayUrl,
                source_url || null,
                settings || {},
                downloadUrl,
                inspectUrl,
                jobId || state.jobId || '',
                { popOutOnReady: true },
            );
        } catch (err) {
            console.error('Result render failed:', err);
        }
        UI.renderTrivia(trivia || null, manifest || null, {
            jobId: jobId || state.jobId || '',
        });

        const resultCard = document.getElementById('resultCard');
        if (resultCard) {
            resultCard.classList.remove('is-loading');
            resultCard.classList.remove('compare-intro-mode');
            applyCompleteProgressUi();
            const progressArea = document.getElementById('progressArea');
            if (progressArea) progressArea.classList.add('hidden');
            showCompleteReadyToast();
            if (usedSimpleDetailExtreme === 'min' || usedSimpleDetailExtreme === 'max') {
                showToast('Tip', 'For more precise detail control, try the nerd panel.', 4200);
                API.logUiFlow('settings', 'simple_detail_tip_shown', usedSimpleDetailExtreme, {
                    edge: usedSimpleDetailExtreme,
                    job_id: jobId || state.jobId || '',
                }).catch(() => {});
            }
            launchMosaicConfetti({
                jobId: jobId || state.jobId || '',
            });
            window.requestAnimationFrame(() => {
                ResultViewer.refitViewer();
                _scrollCardToWorkflowTop();
            });
        } else {
            applyCompleteProgressUi();
            const progressArea = document.getElementById('progressArea');
            if (progressArea) progressArea.classList.add('hidden');
            showCompleteReadyToast();
            if (usedSimpleDetailExtreme === 'min' || usedSimpleDetailExtreme === 'max') {
                showToast('Tip', 'For more precise detail control, try the nerd panel.', 4200);
                API.logUiFlow('settings', 'simple_detail_tip_shown', usedSimpleDetailExtreme, {
                    edge: usedSimpleDetailExtreme,
                    job_id: jobId || state.jobId || '',
                }).catch(() => {});
            }
            launchMosaicConfetti({
                jobId: jobId || state.jobId || '',
            });
        }

        const zoomImg = document.getElementById('zoomModalImg');
        if (zoomImg && inspectUrl) {
            zoomImg.src = inspectUrl;
            zoomImg.onload = () => document.dispatchEvent(new Event('resetZoom'));
        }

        const gen = document.getElementById('generateBtn');
        if (gen) gen.disabled = false;
        const collapsedGen = document.getElementById('settingsCollapsedGenerateBtn');
        if (collapsedGen) collapsedGen.disabled = false;

        const sourcePhotoId = (manifest && manifest.settings && manifest.settings.source_photo_id)
            ? String(manifest.settings.source_photo_id)
            : (state.selectedSourcePhotoId ? String(state.selectedSourcePhotoId) : '');
        if (sourcePhotoId) {
            SourcePhotos.markSourcePhotoUsed(sourcePhotoId);
        }

        // Keep history count/strip responsive even before round-tripping get-history.
        if (effectiveJobId) {
            upsertHistoryItem({
                job_id: effectiveJobId,
                img_url: image_url || preview_url || '',
                thumb_url: preview_url || image_url || '',
                timestamp: Math.floor(Date.now() / 1000),
                source_url: source_url || '',
                status: 'complete',
                social_video: {
                    status: 'queued',
                    run_id: '',
                    error_code: '',
                    video_url: '',
                },
            }, { select: true });
        }

        try {
            const latest = await API.getHistory(1);
            const items = Array.isArray(latest)
                ? latest
                : (latest && Array.isArray(latest.items) ? latest.items : []);
            if (latest && !Array.isArray(latest)) {
                applyHistoryRetentionMeta(latest.retention || null);
                UI.updateHistoryMeta();
            }
            if (latest) {
                const totalVal = Number(latest.total);
                if (Number.isFinite(totalVal)) {
                    const currentTotal = Number(state.historyTotal);
                    const minTotal = Number.isFinite(currentTotal) ? currentTotal : (state.history || []).length;
                    state.historyTotal = Math.max(0, Math.max(totalVal, minTotal));
                }
                const lifetimeTotalVal = Number(latest.lifetime_total);
                if (Number.isFinite(lifetimeTotalVal)) {
                    const currentLifetime = Number(state.historyLifetimeTotal);
                    const nextLifetime = Math.max(
                        0,
                        Math.max(
                            lifetimeTotalVal,
                            Number.isFinite(currentLifetime) ? currentLifetime : 0,
                        ),
                    );
                    state.historyLifetimeTotal = nextLifetime;
                }
            }
            if (items.length > 0) {
                const preferredItem = items.find((item) => String(item && item.job_id || '') === effectiveJobId)
                    || items[0];
                upsertHistoryItem(preferredItem, { select: true, adjustTotals: false });
            }
        } catch (err) {
            // Keep completion UI responsive even if latest-history sync fails.
        }

        refreshSettingsVisibility();
        refreshQuotaAfterCompletion();
        maybePromptGuestSignInAfterGeneration(jobId || state.jobId || '');
        const elapsedMs = getCurrentJobElapsedMs();
        API.logUiFlow('job', 'complete', 'ok', {
            job_id: jobId || state.jobId || '',
        }, elapsedMs).catch(() => {});
        state.currentJobStartedAtMs = null;
        state.currentJobFirstProgressAtMs = null;
        state.currentJobSimpleDetailExtreme = '';
    });

    document.addEventListener('job:cancelled', (e) => {
        ResultViewer.clearLivePreviewState();
        applyCancelledProgressUi();
        const jobId = (e.detail && e.detail.jobId) ? String(e.detail.jobId) : '';
        if (jobId) clearPendingJob(jobId);
        const stopBtn = document.getElementById('stopBtn');
        if (stopBtn) stopBtn.style.display = 'none';
        const resultCard = document.getElementById('resultCard');
        if (resultCard) {
            resultCard.classList.remove('is-loading');
            resultCard.classList.remove('compare-intro-mode');
        }
        const resultLoader = document.getElementById('resultLoader');
        if (resultLoader) resultLoader.classList.add('hidden');
        const progressArea = document.getElementById('progressArea');
        if (progressArea) progressArea.classList.add('hidden');
        const gen = document.getElementById('generateBtn');
        if (gen) gen.disabled = false;
        const collapsedGen = document.getElementById('settingsCollapsedGenerateBtn');
        if (collapsedGen) collapsedGen.disabled = false;
        refreshSettingsVisibility();
        const elapsedMs = getCurrentJobElapsedMs();
        API.logUiFlow('job', 'cancel', 'ok', {
            job_id: jobId || state.jobId || '',
        }, elapsedMs).catch(() => {});
        state.currentJobStartedAtMs = null;
        state.currentJobFirstProgressAtMs = null;
        state.currentJobSimpleDetailExtreme = '';
    });

    document.addEventListener('job:error', (e) => {
        ResultViewer.clearLivePreviewState();
        applyErrorProgressUi();
        const { message, transient } = e.detail || {};
        if (transient) { console.warn(message || UI_COPY.jobStatus.errorFallback); return; }
        const jobId = (e.detail && e.detail.jobId) ? String(e.detail.jobId) : '';
        if (jobId) clearPendingJob(jobId);
        showToast('Job error', message || UI_COPY.jobStatus.errorFallback, { duration: 0, priority: 'error' });
        const stopBtn = document.getElementById('stopBtn');
        if (stopBtn) stopBtn.style.display = 'none';
        const resultCard = document.getElementById('resultCard');
        if (resultCard) {
            resultCard.classList.remove('is-loading');
            resultCard.classList.remove('compare-intro-mode');
        }
        const resultLoader = document.getElementById('resultLoader');
        if (resultLoader) resultLoader.classList.add('hidden');
        const progressArea = document.getElementById('progressArea');
        if (progressArea) progressArea.classList.add('hidden');
        const gen = document.getElementById('generateBtn');
        if (gen) gen.disabled = false;
        const collapsedGen = document.getElementById('settingsCollapsedGenerateBtn');
        if (collapsedGen) collapsedGen.disabled = false;
        refreshSettingsVisibility();
        const elapsedMs = getCurrentJobElapsedMs();
        API.logUiFlow('job', 'complete', 'error', {
            job_id: jobId || state.jobId || '',
            message: message || UI_COPY.jobStatus.errorFallback,
        }, elapsedMs).catch(() => {});
        state.currentJobStartedAtMs = null;
        state.currentJobFirstProgressAtMs = null;
        state.currentJobSimpleDetailExtreme = '';
    });
}
